import React from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

/**
 * FilmGrainOverlay
 *
 * Renders a fixed-position SVG feTurbulence noise overlay across the entire
 * viewport. The noise texture shifts via background-position keyframes using
 * steps(1) timing to create a seed-shift illusion without regenerating
 * multiple SVGs.
 *
 * Design decisions (locked by CONTEXT.md D-01..D-04):
 *   D-01: steps(1) animation — discrete jumps, not a linear pan
 *   D-02: opacity 0.06 + mix-blend-mode: soft-light (CONTEXT.md agreed value)
 *   D-03: fractalNoise baseFrequency 0.65, numOctaves 3, desaturated
 *   D-04: pointer-events: none + aria-hidden — purely decorative
 *
 * Reduced motion: When usePrefersReducedMotion() returns true, the animation
 * is suspended via animation: none. The static grain texture remains visible
 * as a subtle texture rather than disappearing entirely.
 *
 * This component does NOT mount itself. Plan 03 wires it into App.tsx.
 */

// Defined outside the component body to avoid recreation on every render.
const NOISE_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='100%' height='100%' filter='url(#n)' opacity='1'/></svg>`;
const NOISE_DATA_URI = `url("data:image/svg+xml;utf8,${encodeURIComponent(NOISE_SVG)}")`;

const KEYFRAMES_CSS = `
@keyframes filmGrainShift {
  0%   { background-position: 0 0; }
  25%  { background-position: -50px 30px; }
  50%  { background-position: 30px -40px; }
  75%  { background-position: -20px -60px; }
  100% { background-position: 0 0; }
}
.film-grain-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  pointer-events: none;
  mix-blend-mode: soft-light;
  opacity: 0.06;
  background-image: ${NOISE_DATA_URI};
  background-repeat: repeat;
  animation: filmGrainShift 0.8s steps(1) infinite;
  contain: strict;
}
.film-grain-overlay--static {
  animation: none;
}
`;

export const FilmGrainOverlay: React.FC = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <>
      <style>{KEYFRAMES_CSS}</style>
      <div
        className={`film-grain-overlay${prefersReducedMotion ? ' film-grain-overlay--static' : ''}`}
        aria-hidden="true"
      />
    </>
  );
};

export default FilmGrainOverlay;
